// =====================================================================
// src/screens/v2/ProfileTabV2.jsx
//
// V2 Profile Tab — user's own profile (regular user variant).
//
// Sections:
//   - Profile header (avatar, name, bio, profession, status, city)
//   - Stats (Batchmates, Captures, Posts)
//   - Get-listed CTA (for non-business users to register)
//   - Quick links (Connect List, Orders, Help, Settings)
//   - Your batches
//   - Sign out
//
// In Phase B, user data comes from the users collection in Firestore.
// For prototype, all fields are mocked.
//
// Props:
//   onBack             — return wherever user came from
//   onOpenConnectList  — navigate to Connect List
//   onOpenHelp         — navigate to Help & FAQ
//   onOpenBusinessReg  — open business registration flow
// =====================================================================

import React, { useState, useEffect, useMemo } from 'react';
import {
  ArrowLeft, Settings, MapPin, Edit3, Users, Bookmark,
  FileText, Building2, ChevronRight, ShoppingBag,
  Briefcase, LogOut, Repeat
} from 'lucide-react';
import { collection, query, where, getDocs, getCountFromServer } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { loadUserGroups } from '../../lib/db';
import { getBusinessesByOwners, logoBgFromCategory } from '../../lib/v2/firestore';
import './ProfileTabV2.css';

// ============================================================
// Helpers
// ============================================================

// Deterministic gradient picker — same batch always gets the same color.
// V1 doesn't store icon/iconBg fields, so we hash group.id to a small
// palette. Keeps the list visually varied without per-group config.
const BATCH_PALETTE = [
  'linear-gradient(135deg, #3F6BC4 0%, #7193D6 100%)',
  'linear-gradient(135deg, #C45A3F 0%, #E8A57A 100%)',
  'linear-gradient(135deg, #3F8E5C 0%, #7BBE8F 100%)',
  'linear-gradient(135deg, #B8893A 0%, #D6B26B 100%)',
  'linear-gradient(135deg, #7A4FC4 0%, #A887D6 100%)',
];
function gradientForGroup(id) {
  if (!id) return BATCH_PALETTE[0];
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = ((hash << 5) - hash) + id.charCodeAt(i);
    hash |= 0;
  }
  return BATCH_PALETTE[Math.abs(hash) % BATCH_PALETTE.length];
}

// Category → emoji for business logo (shared with Cart + OrderDetail +
// OrderChat + MerchantApp). Kept inline rather than imported to keep the
// V2 screens loosely coupled — easy to swap out if categories change.
const CATEGORY_EMOJI = {
  food_beverages: '🍯',
  apparel: '👗',
  services: '🔧',
  groceries: '🛒',
  electronics: '📱',
  health_beauty: '💄',
  home_decor: '🏡',
};
function emojiForCategory(cat) {
  return CATEGORY_EMOJI[cat] || '🏪';
}

// Item #10 — Batch type → default emoji. Mirrors the BATCH_TYPES table
// in V1's App.jsx (kept inline rather than imported to avoid a cross-
// boundary import for a five-row lookup). Resolution order for the
// tile's icon: group.photoUrl > group.emoji > BATCH_TYPE_EMOJI[type] >
// generic tag glyph. Firestore-loaded groups don't carry the `emoji`
// field by default; only newly-joined-via-invite ones do (per App.jsx
// 7934). So the type fallback is what kicks in for the vast majority
// of tiles — without it every batch resolved to the same '🏠' default
// regardless of category.
const BATCH_TYPE_EMOJI = {
  alma: '🏫',
  company: '💼',
  society: '🏘️',
  family: '👨‍👩‍👧',
  business: '🏢',
};

// ============================================================
// COMPONENT
// ============================================================

export default function ProfileTabV2({
  currentUser, onBack, onOpenConnectList, onOpenHelp, onOpenBusinessReg, onOpenMyOrders,
  onLogout, onOpenSettings, asTab = false,
  // 6.6c additions:
  groups: groupsProp,        // pre-loaded V1 groups; if provided, skip loadUserGroups
  onEditStatus,              // V1 setShowStatusEditor(true)
  onSwitchGroup,             // V1 handleSwitchGroup(batchId)
  onAddGroup,                // V1 join-choice flow
}) {
  const [toast, setToast] = useState(null);

  // ============ DATA LAYER (Step 3.8) ============
  // Identity fields come from currentUser (already loaded by App.jsx).
  // Batches + business ownership are fetched on mount/uid change.
  // V1 stores `groups` as an OBJECT keyed by group id (Object<id, group>),
  // not an array. Convert to array via Object.values() — the rest of this
  // file works with an array shape.
  const [batches, setBatches] = useState(groupsProp ? Object.values(groupsProp) : []);
  const [isBusinessOwner, setIsBusinessOwner] = useState(false);
  // Full list of businesses owned by this user (Step 6.6a.1) — drives the
  // owner-mode card. Empty array means show the Get Listed card instead.
  const [myBusinesses, setMyBusinesses] = useState([]);
  // Owner-card stats: live counts of orders + connections for the
  // businesses this user owns. Originally deferred in 6.6c due to
  // Firestore rules; 6.0d rules update added isBusinessOwner branches
  // to /connections (and /orders already permitted owner reads via
  // isBusinessOwner), so these getCountFromServer calls now work.
  //
  // newChats is intentionally still 0 — proper unread tracking
  // (chats with lastMessageAt > lastReadAt[uid]) requires per-chat
  // reads; deferred.
  const [bizStats, setBizStats] = useState({ orders: 0, connects: 0, newChats: 0 });
  // 6.6c: only show "Loading…" when we're truly starting from scratch.
  // If groupsProp is provided, render immediately.
  const [loading, setLoading] = useState(!groupsProp);

  useEffect(() => {
    if (!currentUser?.id) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        // ----- ownership check (always needed for biz card) -----
        const ownersMap = await getBusinessesByOwners([currentUser.id], { includeUnverified: true });
        if (cancelled) return;
        setIsBusinessOwner(ownersMap.has(currentUser.id));
        setMyBusinesses(ownersMap.get(currentUser.id) || []);

        // ----- batches: only refetch when caller didn't pre-load them -----
        // groupsProp from App.jsx is already live (V1's subscription) and
        // shaped as an object keyed by group id — convert to array here.
        // Deep ?v2=profile-v2 route → no prop → fetch on our own.
        let groupList;
        if (groupsProp) {
          groupList = Object.values(groupsProp);
        } else {
          const groupsRaw = await loadUserGroups(currentUser.id);
          if (cancelled) return;
          groupList = Array.isArray(groupsRaw) ? groupsRaw : [];
        }

        // The group docs' memberCount field is often STALE (e.g. shows 6
        // when the live subscription returns 10 — confirmed in Step 3.7).
        // Fetch the live approved count per group, matching V1's filter
        // EXACTLY: query status='active', then client-side filter for
        // approvedAt != null. This mirrors subscribeToGroupMembers in db.js
        // (line 1185-1192). Counting all active docs would over-count by
        // any pending-approval members.
        const liveCounts = await Promise.all(
          groupList.map(async (g) => {
            try {
              const q = query(
                collection(db, 'groups', g.id, 'members'),
                where('status', '==', 'active'),
              );
              const snap = await getDocs(q);
              return snap.docs.filter(d => d.data().approvedAt).length;
            } catch (err) {
              // Permission denied is expected for non-approved viewers (rules
              // block them from reading members). Suppress the console noise;
              // fall back to the stale group.memberCount either way. Any other
              // error still surfaces so genuine breakage isn't hidden.
              if (err?.code !== 'permission-denied') {
                console.warn('[ProfileTabV2] member count failed for', g.id, err);
              }
              return g.memberCount || 0;
            }
          })
        );
        if (cancelled) return;

        // Merge live counts onto each group, replacing the stale field
        const merged = groupList.map((g, i) => ({
          ...g,
          memberCount: liveCounts[i],
        }));

        setBatches(merged);
      } catch (err) {
        console.error('[ProfileTabV2] load failed:', err);
        if (!cancelled) {
          // Don't clobber groupsProp-seeded state on error in tab mode
          if (!groupsProp) setBatches([]);
          setIsBusinessOwner(false);
          setMyBusinesses([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [currentUser?.id, groupsProp]);

  // Live owner-card stats (6.0d). Re-runs when the owned-businesses list
  // changes. Uses getCountFromServer for cheap aggregations — single
  // query per metric. Firestore `in` clause supports up to 30 values;
  // owners with more get the first 30 only (none at V2.0 scale).
  useEffect(() => {
    if (!myBusinesses || myBusinesses.length === 0) {
      setBizStats({ orders: 0, connects: 0, newChats: 0 });
      return undefined;
    }
    const bizIds = myBusinesses.map(b => b.id).filter(Boolean).slice(0, 30);
    if (bizIds.length === 0) return undefined;

    let cancelled = false;
    (async () => {
      try {
        const [ordersSnap, connectsSnap] = await Promise.all([
          getCountFromServer(query(
            collection(db, 'orders'),
            where('businessId', 'in', bizIds)
          )),
          getCountFromServer(query(
            collection(db, 'connections'),
            where('businessId', 'in', bizIds)
          )),
        ]);
        if (cancelled) return;
        setBizStats({
          orders: ordersSnap.data().count,
          connects: connectsSnap.data().count,
          newChats: 0,   // TBD — needs per-chat lastReadAt comparison
        });
      } catch (err) {
        // Non-fatal — stats stay at zero. If rules regress, console will warn.
        console.warn('[ProfileTabV2] biz stats failed:', err?.message);
      }
    })();
    return () => { cancelled = true; };
  }, [myBusinesses]);

  // Auto-hide toast
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2200);
    return () => clearTimeout(t);
  }, [toast]);

  function notImpl(label) {
    return () => setToast(`${label} — coming in next checkpoint`);
  }

  // Stub-cleanup: helper that returns either the real prop callback or
  // a "coming soon" toast. Using `||` directly on the call site
  // (`onClick={() => fn?.() || notImpl(...)()}`) was firing the toast
  // even when the prop WAS provided — because `fn()` returns
  // undefined, the `||` short-circuit always evaluated the right
  // side. Pre-resolving the handler avoids that whole class of bug.
  function action(fn, label) {
    return fn || notImpl(label);
  }

  // Derive "Member since" string from onboardedAt (Firestore Timestamp or ISO)
  const memberSince = useMemo(() => {
    const raw = currentUser?.onboardedAt;
    if (!raw) return '';
    try {
      let d;
      if (typeof raw === 'string') d = new Date(raw);
      else if (raw.toDate) d = raw.toDate();
      else if (raw.toMillis) d = new Date(raw.toMillis());
      else d = new Date(raw);
      if (isNaN(d.getTime())) return '';
      return d.toLocaleString('en-US', { month: 'long', year: 'numeric' });
    } catch {
      return '';
    }
  }, [currentUser?.onboardedAt]);

  // Rough batchmates count: sum of memberCounts minus one self-count per batch.
  // Imperfect for users in overlapping batches (counts duplicates) but
  // directionally correct and fast — no extra reads.
  const batchmatesCount = useMemo(() => {
    const total = batches.reduce((s, g) => s + (g.memberCount || 0), 0);
    return Math.max(0, total - batches.length);
  }, [batches]);

  // Build the display object in the original MOCK_USER shape so the JSX
  // below stays unchanged. Real fields populated from currentUser + Firestore.
  // captures/posts stay 0 — V1 doesn't expose these as queryable counts yet.
  const user = useMemo(() => ({
    name: currentUser?.name || '',
    initial: currentUser?.initials || (currentUser?.name || '?').charAt(0).toUpperCase(),
    photoUrl: currentUser?.photoUrl || null,
    avatarBg: currentUser?.color || '#C45A3F',
    memberSince,
    bio: currentUser?.bio || '',
    profession: currentUser?.profession || '',
    status: currentUser?.status || '',
    city: currentUser?.currentCity || '',
    stats: {
      batchmates: batchmatesCount,
      captures: 0,
      posts: 0,
    },
    batches: batches.map(b => ({
      id: b.id,
      name: b.name || 'Batch',
      members: b.memberCount || 0,
      // Item #10 — Resolution order: explicit emoji on the group doc,
      // then the type-default from BATCH_TYPE_EMOJI, then a neutral
      // tag glyph. Earlier hardcoded '🏠' made every tile look the
      // same; this lets the school batches show 🏫, family groups
      // show 👨‍👩‍👧 etc. without requiring backfill of the emoji field
      // on existing group docs.
      icon: b.emoji || BATCH_TYPE_EMOJI[b.type] || '🏷️',
      // Some groups have a photoUrl (set via the BatchCreatedCelebration
      // upload flow in V1). When present, the tile renders the photo in
      // place of the emoji — forward-compatible with batches that have
      // covers, falls back gracefully when missing.
      photoUrl: b.photoUrl || null,
      iconBg: gradientForGroup(b.id),
      // V1 stores adminIds on the group doc (per createGroup in db.js).
      // Falls back to 'Member' if the field is missing.
      role: Array.isArray(b.adminIds) && b.adminIds.includes(currentUser?.id)
        ? 'Admin'
        : 'Member',
    })),
    isBusinessOwner,
    myBusinesses,
  }), [currentUser, memberSince, batchmatesCount, batches, isBusinessOwner, myBusinesses]);

  return (
    <div className={`v2-profile-tab${asTab ? ' as-tab' : ''}`}>
      {/* Top bar with back + settings */}
      <div className="v2pt-top-bar">
        {!asTab && onBack && (
          <button className="v2pt-icon-btn" onClick={onBack} aria-label="Back">
            <ArrowLeft size={20} />
          </button>
        )}
        <div style={{ flex: 1 }} />
        <button
          className="v2pt-icon-btn"
          onClick={action(onOpenSettings, 'Settings')}
          aria-label="Settings"
        >
          <Settings size={18} />
        </button>
      </div>

      <div className="v2pt-scroll-body">

        {/* ============ HEADER ============ */}
        <div className="v2pt-header">
          <div className="v2pt-id-row">
            {user.photoUrl ? (
              <img
                className="v2pt-avatar"
                src={user.photoUrl}
                alt={user.name}
                style={{ objectFit: 'cover' }}
              />
            ) : (
              <div className="v2pt-avatar" style={{ background: user.avatarBg }}>
                {user.initial}
              </div>
            )}
            <div className="v2pt-since">
              <div className="v2pt-since-cap">Member since</div>
              <div className="v2pt-since-val">{user.memberSince}</div>
            </div>
          </div>

          <h1 className="v2pt-name">{user.name}</h1>
          <p className="v2pt-bio">{user.bio}</p>

          <div className="v2pt-prof-pill">
            <Briefcase size={11} />
            {user.profession}
          </div>

          <button className="v2pt-status-editor" onClick={action(onEditStatus, 'Edit status')}>
            <span className="v2pt-status-icon">⚡</span>
            <span className="v2pt-status-text">"{user.status}"</span>
            <Edit3 size={13} className="v2pt-edit-icon" />
          </button>

          <div className="v2pt-city-row">
            <MapPin size={13} />
            Currently in {user.city}
          </div>
        </div>

        {/* ============ STATS ============ */}
        <div className="v2pt-stats-grid">
          <div className="v2pt-stat">
            <div className="v2pt-stat-num">{user.stats.batchmates}</div>
            <div className="v2pt-stat-label">
              <Users size={11} /> Batchmates
            </div>
          </div>
          <div className="v2pt-stat">
            <div className="v2pt-stat-num">{user.stats.captures}</div>
            <div className="v2pt-stat-label">
              <Bookmark size={11} /> Captures
            </div>
          </div>
          <div className="v2pt-stat">
            <div className="v2pt-stat-num">{user.stats.posts}</div>
            <div className="v2pt-stat-label">
              <FileText size={11} /> Posts
            </div>
          </div>
        </div>

        {/* ============ BUSINESS OWNER CARD (6.6a.1) ============ */}
        {/* Shown when user owns ≥1 business. For single-business owners
            the card shows the shop's name + category emoji. For multi-
            owners (e.g. Test1 owns 3) it collapses to "N businesses ·
            comma-separated names + overflow". Stats (Orders / Connects /
            New Chats) are placeholder zeros for now — real counts wired
            in 6.0d alongside the business switcher in MerchantApp. */}
        {user.isBusinessOwner ? (
          <div className="v2pt-biz-card">
            <div className="v2pt-biz-head">
              <div
                className="v2pt-biz-logo"
                style={{
                  background: user.myBusinesses.length === 1
                    ? logoBgFromCategory(user.myBusinesses[0].category)
                    : 'linear-gradient(135deg, #B8893A 0%, #D6B576 100%)',
                }}
              >
                {user.myBusinesses.length === 1
                  ? emojiForCategory(user.myBusinesses[0].category)
                  : '🏪'}
              </div>
              <div className="v2pt-biz-info">
                <div className="v2pt-biz-name">
                  {user.myBusinesses.length === 1
                    ? user.myBusinesses[0].name
                    : `${user.myBusinesses.length} businesses`}
                </div>
                <div className="v2pt-biz-sub">
                  {user.myBusinesses.length === 1
                    ? (user.myBusinesses[0].status === 'verified'
                        ? '✓ Verified · Live on Aangan Connect'
                        : 'Pending verification')
                    : (
                        user.myBusinesses.slice(0, 2).map(b => b.name).join(' · ')
                        + (user.myBusinesses.length > 2 ? ` +${user.myBusinesses.length - 2}` : '')
                      )}
                </div>
              </div>
            </div>

            <div className="v2pt-biz-stats">
              <div className="v2pt-biz-stat">
                <div className="v2pt-biz-stat-num">{bizStats.orders}</div>
                <div className="v2pt-biz-stat-label">Orders</div>
              </div>
              <div className="v2pt-biz-stat">
                <div className="v2pt-biz-stat-num">{bizStats.connects}</div>
                <div className="v2pt-biz-stat-label">Connects</div>
              </div>
              <div className="v2pt-biz-stat">
                <div className="v2pt-biz-stat-num">{bizStats.newChats}</div>
                <div className="v2pt-biz-stat-label">New Chats</div>
              </div>
            </div>

            <button
              className="v2pt-biz-switch"
              onClick={() => { window.location.search = '?v2=merchant'; }}
            >
              <Repeat size={14} /> Switch to business mode
            </button>
          </div>
        ) : (
          <button
            className="v2pt-get-listed"
            onClick={action(onOpenBusinessReg, 'Business registration')}
          >
            <div className="v2pt-gl-icon">
              <Building2 size={20} />
            </div>
            <div className="v2pt-gl-text">
              <div className="v2pt-gl-title">Have a business? Get listed on Aangan</div>
              <div className="v2pt-gl-sub">Reach customers on Aangan · Get listed in minutes</div>
            </div>
            <ChevronRight size={16} className="v2pt-gl-chev" />
          </button>
        )}

        {/* ============ QUICK LINKS ============ */}
        <div className="v2pt-cap">Quick access</div>
        <div className="v2pt-quick-links">
          <button
            className="v2pt-ql-row"
            onClick={action(onOpenConnectList, 'Connect List')}
          >
            <div className="v2pt-ql-icon" style={{ background: 'var(--gold)' }}>
              <Bookmark size={14} />
            </div>
            <div className="v2pt-ql-text">
              <div className="v2pt-ql-title">My Connect List</div>
              <div className="v2pt-ql-sub">Businesses you've connected with</div>
            </div>
            <ChevronRight size={16} />
          </button>
          <button className="v2pt-ql-row" onClick={action(onOpenMyOrders, 'My Orders')}>
            <div className="v2pt-ql-icon" style={{ background: 'var(--accent)' }}>
              <ShoppingBag size={14} />
            </div>
            <div className="v2pt-ql-text">
              <div className="v2pt-ql-title">My Orders</div>
              <div className="v2pt-ql-sub">Past and current orders</div>
            </div>
            <ChevronRight size={16} />
          </button>
          {/* Help & FAQ intentionally NOT in Quick Access — V1's Settings →
              Help & support is the established path. Keeping the Quick
              Access list focused on V2-specific destinations (Connect List,
              Orders) avoids duplicating navigation. */}
        </div>

        {/* ============ BATCHES ============ */}
        <div className="v2pt-cap">Your batches</div>
        <div className="v2pt-batches">
          {user.batches.map(b => (
            <button
              key={b.id}
              className="v2pt-batch-row"
              onClick={onSwitchGroup ? () => onSwitchGroup(b.id) : notImpl(`Open ${b.name}`)}
            >
              {/* Item #10 — render photo when the batch has a cover,
                  otherwise the emoji on the gradient tile. img sits
                  inside the same .v2pt-batch-icon container so it
                  inherits the existing square + border-radius CSS. */}
              <div
                className="v2pt-batch-icon"
                style={{ background: b.photoUrl ? 'transparent' : b.iconBg, overflow: 'hidden' }}
              >
                {b.photoUrl ? (
                  <img
                    src={b.photoUrl}
                    alt=""
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: 'inherit',
                      display: 'block',
                    }}
                  />
                ) : (
                  b.icon
                )}
              </div>
              <div className="v2pt-batch-text">
                <div className="v2pt-batch-name">{b.name}</div>
                <div className="v2pt-batch-sub">
                  {b.members} members · {b.role}
                </div>
              </div>
              <ChevronRight size={16} />
            </button>
          ))}
          <button className="v2pt-add-batch" onClick={action(onAddGroup, 'Add another batch')}>
            + Add another batch
          </button>
        </div>

        {/* ============ SIGN OUT ============ */}
        <button
          className="v2pt-signout"
          onClick={action(onLogout, 'Sign out')}
        >
          <LogOut size={14} /> Sign out
        </button>

      </div>

      {toast && <div className="v2pt-toast">{toast}</div>}
    </div>
  );
}
